import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Envelope, Key, ArrowLeft } from '@phosphor-icons/react';
import { errorStatus, extractErrorMessage } from '../lib/error';
import { requestOtp, verifyOtp } from '../services/api/auth.api';
import { useGuestAuth } from '../contexts/GuestAuthContext';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Client resend cooldown — mirrors the leads OtpVerifyPanel + backend throttle. */
const COOLDOWN_SECONDS = 30;
/** Same-origin internal path only (no open redirect). */
const safeReturnTo = (v: string | null): string =>
  v && v.startsWith('/') && !v.startsWith('//') ? v : '/';

/**
 * Passwordless guest sign-in: email → 6-digit code. No password, no separate sign-up
 * (first verify creates the account). Uses the storefront guest session (lib/auth.ts).
 */
export default function GuestLoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'));
  const { login } = useGuestAuth();

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [resendConfirm, setResendConfirm] = useState<string | null>(null);

  // Single cooldown interval, cleared on unmount and when it hits 0.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const request = useMutation({
    mutationFn: () => requestOtp(email.trim()),
    onSuccess: (data) => {
      setStep('code');
      if (data.devCode) setCode(data.devCode); // non-prod convenience only
    },
  });

  const verify = useMutation({
    mutationFn: () => verifyOtp(email.trim(), code),
    onSuccess: async (tokens) => {
      await login(tokens);
      navigate(returnTo, { replace: true });
    },
  });

  function submitEmail(e: FormEvent) {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) return;
    request.mutate();
  }
  function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return;
    verify.mutate();
  }
  function handleResend() {
    if (request.isPending || cooldown > 0) return;
    setResendConfirm(null);
    request.mutate(undefined, {
      // Positive signal a new code went out; start the cooldown so repeated
      // taps can't hammer the endpoint. devCode (non-prod) is handled by the
      // mutation-level onSuccess.
      onSuccess: () => {
        setResendConfirm(`We sent a new code to ${email.trim()}.`);
        setCooldown(COOLDOWN_SECONDS);
      },
      // Only lock the button on a rate-limit; a transient 500/network error
      // means no code was sent, so the guest should be able to retry at once.
      onError: (err) => { if (errorStatus(err) === 429) setCooldown(COOLDOWN_SECONDS); },
    });
  }

  const reqErr = request.isError
    ? errorStatus(request.error) === 429 ? 'Please wait a moment before requesting another code.'
      : extractErrorMessage(request.error)
    : null;
  const verErr = verify.isError
    ? errorStatus(verify.error) === 429 ? 'Too many attempts — please wait a moment.'
      : 'That code is invalid or has expired.'
    : null;

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
        {step === 'email' ? (
          <>
            <Envelope className="mx-auto h-12 w-12 text-brand-blue" />
            <h1 className="mt-4 text-center text-xl font-bold text-brand-navy">Sign in to BestAcco</h1>
            <p className="mt-1 text-center text-sm text-gray-500">Enter your email — we'll send a 6-digit code. No password needed.</p>
            <form onSubmit={submitEmail} noValidate className="mt-6">
              {reqErr && <p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{reqErr}</p>}
              <label htmlFor="email" className="text-sm font-semibold text-brand-navy">Email</label>
              <input id="email" name="email" type="email" autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-brand-blue" />
              <button type="submit" disabled={request.isPending || !EMAIL_RE.test(email.trim())}
                className="mt-4 w-full rounded-xl bg-brand-blue px-6 py-3 font-semibold text-white hover:bg-brand-blue-dark disabled:opacity-60">
                {request.isPending ? 'Sending…' : 'Continue'}
              </button>
            </form>
          </>
        ) : (
          <>
            <Key className="mx-auto h-12 w-12 text-brand-blue" />
            <h1 className="mt-4 text-center text-xl font-bold text-brand-navy">Enter your code</h1>
            <p className="mt-1 text-center text-sm text-gray-500">We sent a 6-digit code to <strong>{email}</strong>.</p>
            <form onSubmit={submitCode} noValidate className="mt-6">
              {verErr && <p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{verErr}</p>}
              <label htmlFor="code" className="text-sm font-semibold text-brand-navy">6-digit code</label>
              <input id="code" name="code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 tracking-[0.5em] text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-brand-blue" />
              <button type="submit" disabled={verify.isPending || code.length !== 6}
                className="mt-4 w-full rounded-xl bg-brand-blue px-6 py-3 font-semibold text-white hover:bg-brand-blue-dark disabled:opacity-60">
                {verify.isPending ? 'Verifying…' : 'Verify & sign in'}
              </button>
            </form>
            {resendConfirm && !reqErr && (
              <p role="status" className="mt-3 text-center text-sm font-medium text-brand-navy">{resendConfirm}</p>
            )}
            <div className="mt-4 flex items-center justify-between text-sm">
              <button type="button" onClick={() => { setStep('email'); setCode(''); setResendConfirm(null); verify.reset(); }} className="inline-flex items-center gap-1 font-semibold text-brand-blue hover:underline">
                <ArrowLeft className="h-4 w-4" /> Different email
              </button>
              <button type="button" disabled={request.isPending || cooldown > 0} onClick={handleResend} className="font-semibold text-brand-blue hover:underline disabled:cursor-not-allowed disabled:opacity-60">
                {request.isPending ? 'Sending…' : cooldown > 0 ? `Resend available in 0:${String(cooldown).padStart(2, '0')}` : 'Resend code'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
